import React from 'react';
import { ShortCode } from '@highereducation/wordpress';
import { ExpertBox } from '@highereducation/hen-ui';
import type { ExpertByLineProps } from '@highereducation/hen-ui';

type ExpertBoxExpertShortcodeProps = {
  name?: string;
  role?: string;
  'avatar-src'?: string;
  'avatar-fallback'?: string;
  href?: string;
  [key: string]: unknown;
};

type ExpertBoxShortcodeProps = {
  title?: string;
  brow?: string;
  question?: string;
  'action-label'?: string;
  'action-href'?: string;
  'source-label'?: string;
  'source-href'?: string;
  variant?: 'default' | 'plain';
  children?: React.ReactNode;
  [key: string]: unknown;
};

/**
 * ExpertBoxShortcode - Shortcode wrapper for ExpertBox. Use [hen-ui-expert-box-expert] for each expert.
 *
 * @example
 * [hen-ui-expert-box title="Our Experts" brow="Featured" question="Who contributed?" action-label="View all" action-href="/experts"]
 *   [hen-ui-expert-box-expert name="Dr. Jane Smith" role="Clinical Psychologist" avatar-src="..." href="/profile"]
 *   [hen-ui-expert-box-expert name="Dr. John Doe" role="Researcher" avatar-src="..."]
 *   Optional body content or source text here.
 * [/hen-ui-expert-box]
 */
export const ExpertBoxShortcode: ShortCode<ExpertBoxShortcodeProps> = {
  component: ({
    title = '',
    brow,
    question,
    'action-label': actionLabel,
    'action-href': actionHref,
    'source-label': sourceLabel,
    'source-href': sourceHref,
    variant = 'default',
    children,
  }) => {
    const childArray = React.Children.toArray(children);
    const experts: ExpertByLineProps[] = [];
    const contentNodes: React.ReactNode[] = [];

    childArray.forEach((child) => {
      if (React.isValidElement(child)) {
        const p = child.props as ExpertBoxExpertShortcodeProps;
        if (typeof p?.name === 'string' && typeof p?.role === 'string') {
          experts.push({
            name: p.name,
            role: p.role,
            ...(p['avatar-src'] && { avatarSrc: p['avatar-src'] }),
            ...(p['avatar-fallback'] && {
              avatarFallback: p['avatar-fallback'],
            }),
            ...(p.href && { href: p.href }),
          });
        } else {
          contentNodes.push(child);
        }
      } else {
        contentNodes.push(child);
      }
    });

    if (!title) return null;

    const source =
      sourceLabel && sourceHref
        ? { label: sourceLabel, href: sourceHref }
        : undefined;

    return (
      <ExpertBox
        title={title}
        brow={brow}
        question={question}
        actionLabel={actionLabel}
        actionHref={actionHref}
        source={source}
        variant={variant}
        experts={experts}
      >
        {contentNodes.length > 0 ? contentNodes : undefined}
      </ExpertBox>
    );
  },
};

export { ExpertBoxExpertShortcode } from './ExpertBoxExpertShortcode';
